/**
 * Restores full tenant/agent objects from persisted IDs after page refresh.
 * IDs are rehydrated from localStorage by ContextStore; this hook re-fetches
 * the matching objects once the tenant/agent lists are available.
 */
import { useEffect } from 'react';
import { useContextStore } from '@app/store/context.store';
import { useTenants } from '@features/tenants/hooks/useTenants';
import { useAgents } from '@features/agents/hooks/useAgents';
import { isSuperAdmin, canActAsTenant, can } from '@features/auth/roles';
import { useAuthStore } from '@app/store/auth.store';

export function useContextHydration() {
  const user = useAuthStore((s) => s.user);
  const {
    activeTenantId,
    activeTenant,
    activeAgentId,
    activeAgent,
    restoreActiveTenant,
    setActiveAgent,
    clearActiveAgent,
  } = useContextStore();

  const { tenants, loading: tenantsLoading } = useTenants({
    enabled: canActAsTenant(user) || isSuperAdmin(user),
  });
  const { agents, loading: agentsLoading } = useAgents({
    // Only fetch agents once a tenant is active — otherwise the request goes out
    // without an x-tenant-id header and 400s (part of the login/refresh burst).
    enabled:
      !!activeTenantId &&
      (can(user, 'tenant.agents.read') ||
        Object.keys(user?.agentGrants ?? {}).length > 0),
  });

  // Restore tenant object from the tenants list (platform act-as).
  useEffect(() => {
    if (activeTenant || !activeTenantId || tenantsLoading) return;
    if (!canActAsTenant(user) && !isSuperAdmin(user)) return;

    const match = tenants.find((t) => t.id === activeTenantId);
    if (match) restoreActiveTenant(match);
  }, [
    activeTenant,
    activeTenantId,
    tenants,
    tenantsLoading,
    user,
    restoreActiveTenant,
  ]);

  // Restore agent object from the agents list (scoped to active tenant).
  useEffect(() => {
    if (!activeAgentId || agentsLoading || activeAgent) return;

    const agent = agents.find((a) => a.id === activeAgentId);
    if (agent) setActiveAgent(agent);
    else clearActiveAgent();
  }, [
    activeAgentId,
    activeAgent,
    agents,
    agentsLoading,
    setActiveAgent,
    clearActiveAgent,
  ]);
}
